import { useState, useEffect } from "react";
import { 
  Home, Building2, MapPin, Phone, Zap, Car, Bath, 
  Ruler, Calendar, User, Leaf, TrendingDown, Info, Sparkles, Loader2
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import ImageUploaderModern from "./ImageUploaderModern";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Property categories - only sale types
const PROPERTY_CATEGORIES = [
  { value: "apartament", label: "Apartament", icon: Building2, description: "2+ camere" },
  { value: "garsoniera", label: "Garsonieră", icon: Home, description: "1 cameră" },
  { value: "casa", label: "Casă / Vilă", icon: Home, description: "Curte proprie" },
  { value: "teren", label: "Teren", icon: MapPin, description: "Intravilan/Extravilan" },
];

// Compartimentare
const COMPARTMENT_OPTIONS = [
  { value: "decomandat", label: "Decomandat" },
  { value: "semidecomandat", label: "Semidecomandat" },
  { value: "nedecomandat", label: "Nedecomandat" },
  { value: "circular", label: "Circular" },
];

// Confort
const COMFORT_OPTIONS = [
  { value: "lux", label: "Lux" },
  { value: "1", label: "Confort 1" },
  { value: "2", label: "Confort 2" },
  { value: "3", label: "Confort 3" },
];

// Building type
const BUILDING_TYPE_OPTIONS = [
  { value: "bloc_vechi", label: "Bloc vechi" },
  { value: "bloc_nou", label: "Bloc nou / Rezidențial" },
  { value: "casa", label: "Casă individuală" },
  { value: "vila", label: "Vilă" },
  { value: "duplex", label: "Duplex / Triplex" },
];

// Property condition
const CONDITION_OPTIONS = [
  { value: "nou", label: "Nou / Finalizat recent" },
  { value: "renovat", label: "Renovat" },
  { value: "buna", label: "Stare bună" },
  { value: "necesita_renovare", label: "Necesită renovare" },
  { value: "la_rosu", label: "La roșu / Semifinisat" },
];

// Furnished options
const FURNISHED_OPTIONS = [
  { value: "mobilat_utilat", label: "Mobilat și utilat complet" },
  { value: "partial", label: "Parțial mobilat" },
  { value: "utilat", label: "Doar utilat (electrocasnice)" },
  { value: "nemobilat", label: "Nemobilat / Gol" },
];

// Energy class with benefits info
const ENERGY_CLASSES = [
  { value: "A", label: "Clasa A", color: "bg-green-500", benefit: "Reducere dobândă până la 0.3%" },
  { value: "B", label: "Clasa B", color: "bg-green-400", benefit: "Reducere dobândă până la 0.2%" },
  { value: "C", label: "Clasa C", color: "bg-lime-400", benefit: "Reducere dobândă până la 0.1%" },
  { value: "D", label: "Clasa D", color: "bg-yellow-400", benefit: null },
  { value: "E", label: "Clasa E", color: "bg-orange-400", benefit: null },
  { value: "F", label: "Clasa F", color: "bg-orange-500", benefit: null },
  { value: "G", label: "Clasa G", color: "bg-red-500", benefit: null },
];

// Floor options
const FLOOR_OPTIONS = [
  { value: "demisol", label: "Demisol" },
  { value: "parter", label: "Parter" },
  ...Array.from({ length: 15 }, (_, i) => ({ value: String(i + 1), label: `Etaj ${i + 1}` })),
  { value: "mansarda", label: "Mansardă" },
];

const DEFAULT_FORM = {
  title: "",
  description: "",
  price: "",
  currency: "EUR",
  property_type: "apartament",
  rooms: 2,
  bathrooms: 1,
  surface: "",
  useful_surface: "",
  zone: "",
  city: "București",
  county: "București",
  images: [],
  phone: "",
  contact_name: "",
  compartment: "",
  comfort: "",
  building_type: "",
  floor: "",
  total_floors: "",
  year_built: "",
  property_condition: "",
  furnished: "",
  energy_class: "",
  parking: false,
  parking_spots: "",
  negotiable: false,
  accepts_exchange: false,
};

const ListingForm = ({ 
  listing = null, 
  onSave, 
  onCancel,
  saving = false,
}) => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [generatingAI, setGeneratingAI] = useState(false);

  useEffect(() => {
    if (listing) {
      setForm({
        ...DEFAULT_FORM,
        ...listing,
        price: listing.price?.toString() || "",
        surface: listing.surface?.toString() || "",
        useful_surface: listing.useful_surface?.toString() || "",
        rooms: listing.rooms || 2,
        bathrooms: listing.bathrooms || 1,
        floor: listing.floor?.toString() || "",
        total_floors: listing.total_floors?.toString() || "",
        year_built: listing.year_built?.toString() || "",
        energy_class: listing.energy_certificate || listing.energy_class || "",
        contact_name: listing.contact_name || listing.owner_name || "",
        phone: listing.phone || listing.owner_phone || "",
        parking: listing.parking || false,
        parking_spots: listing.parking_spots?.toString() || "",
        negotiable: listing.negotiable || false,
        accepts_exchange: listing.accepts_exchange || false,
      });
    }
  }, [listing]);

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const generateAIDescription = async () => {
    if (!form.property_type || !form.city) {
      toast.error("Completează tipul proprietății și orașul pentru a genera descrierea");
      return;
    }

    setGeneratingAI(true);
    try {
      const features = [];
      if (form.compartment) features.push(form.compartment);
      if (form.comfort) features.push(`Confort ${form.comfort}`);
      if (form.building_type) features.push(form.building_type.replace(/_/g, ' '));
      if (form.property_condition) features.push(form.property_condition.replace(/_/g, ' '));
      if (form.furnished) features.push(form.furnished.replace(/_/g, ' '));
      if (form.parking) features.push("loc parcare");
      if (form.energy_class) features.push(`Clasă energetică ${form.energy_class}`);

      const response = await axios.post(`${API}/ai/description`, {
        property_type: form.property_type,
        rooms: form.rooms || 2,
        surface: parseFloat(form.surface) || 50,
        zone: form.zone || "",
        city: form.city,
        features: features,
        price: parseFloat(form.price) || 0
      });

      if (response.data.description) {
        update("description", response.data.description);
        toast.success("Descriere generată cu AI!");
      }
    } catch (error) {
      console.error("AI generation error:", error);
      toast.error(error.response?.data?.detail || "Eroare la generarea descrierii");
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSubmit = () => {
    if (!form.title.trim()) return alert("Introduceți titlul anunțului");
    if (!form.price) return alert("Introduceți prețul");
    if (!form.phone.trim()) return alert("Introduceți numărul de telefon");

    onSave({
      ...form,
      price: parseFloat(form.price),
      surface: parseFloat(form.surface) || 0,
      useful_surface: parseFloat(form.useful_surface) || 0,
      rooms: parseInt(form.rooms),
      bathrooms: parseInt(form.bathrooms) || 1,
      total_floors: form.total_floors?.toString() || "",
      year_built: form.year_built?.toString() || "",
      parking_spots: parseInt(form.parking_spots) || null,
      energy_certificate: form.energy_class || null,
    });
  };

  const selectedEnergyClass = ENERGY_CLASSES.find(e => e.value === form.energy_class);

  return (
    <div className="space-y-8">
      
      {/* SECTION: Property Type */}
      <section>
        <h3 className="text-lg font-semibold text-stone-800 mb-4">Ce vinzi?</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {PROPERTY_CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const isSelected = form.property_type === cat.value;
            return (
              <button
                key={cat.value}
                type="button"
                onClick={() => update("property_type", cat.value)}
                className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                  isSelected
                    ? "border-emerald-500 bg-emerald-50 shadow-sm"
                    : "border-stone-200 hover:border-stone-300 bg-white"
                }`}
              >
                <Icon className={`w-6 h-6 mb-2 ${isSelected ? "text-emerald-600" : "text-stone-400"}`} />
                <div className={`font-medium ${isSelected ? "text-emerald-700" : "text-stone-700"}`}>
                  {cat.label}
                </div>
                <div className="text-xs text-stone-500">{cat.description}</div>
                {isSelected && (
                  <div className="absolute top-2 right-2 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* SECTION: Basic Info */}
      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-stone-800">Informații de bază</h3>
        
        <div>
          <Label className="text-stone-700">
            Titlu anunț <span className="text-red-500">*</span>
            <span className="text-stone-400 text-xs ml-2">{form.title.length}/80</span>
          </Label>
          <Input
            placeholder="ex: Apartament 3 camere Floreasca, vedere parc, renovat 2024"
            value={form.title}
            onChange={(e) => update("title", e.target.value.slice(0, 80))}
            className="mt-1.5"
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <Label className="text-stone-700">
              Preț vânzare <span className="text-red-500">*</span>
            </Label>
            <Input
              type="number"
              placeholder="85000"
              value={form.price}
              onChange={(e) => update("price", e.target.value)}
              className="mt-1.5 text-lg font-semibold"
            />
          </div>
          <div>
            <Label className="text-stone-700">Monedă</Label>
            <Select value={form.currency} onValueChange={(v) => update("currency", v)}>
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EUR">EUR €</SelectItem>
                <SelectItem value="RON">RON lei</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-6 pt-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox 
              checked={form.negotiable} 
              onCheckedChange={(v) => update("negotiable", v)}
            />
            <span className="text-sm text-stone-600">Preț negociabil</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox 
              checked={form.accepts_exchange} 
              onCheckedChange={(v) => update("accepts_exchange", v)}
            />
            <span className="text-sm text-stone-600">Accept schimb</span>
          </label>
        </div>
      </section>

      {/* SECTION: Location */}
      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-emerald-600" />
          Locație
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-stone-700">Oraș <span className="text-red-500">*</span></Label>
            <Input
              placeholder="București"
              value={form.city}
              onChange={(e) => update("city", e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-stone-700">Zonă / Cartier</Label>
            <Input
              placeholder="ex: Floreasca, Herăstrău"
              value={form.zone}
              onChange={(e) => update("zone", e.target.value)}
              className="mt-1.5"
            />
          </div>
        </div>
      </section>

      {/* SECTION: Property Details */}
      {form.property_type !== "teren" && (
        <section className="space-y-4">
          <h3 className="text-lg font-semibold text-stone-800">Detalii proprietate</h3>
          
          {/* Rooms selector */}
          <div>
            <Label className="text-stone-700 mb-2 block">Număr camere</Label>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => update("rooms", n)}
                  className={`w-12 h-12 rounded-lg font-semibold transition-all ${
                    form.rooms === n
                      ? "bg-emerald-500 text-white shadow-md"
                      : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <Label className="text-stone-700">Suprafață (m²)</Label>
              <Input
                type="number"
                placeholder="65"
                value={form.surface}
                onChange={(e) => {
                  update("surface", e.target.value);
                  update("useful_surface", e.target.value);
                }}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-stone-700">Băi</Label>
              <Select value={form.bathrooms?.toString()} onValueChange={(v) => update("bathrooms", parseInt(v))}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Nr." />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4].map(n => (
                    <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-stone-700">Etaj</Label>
              <Select value={form.floor} onValueChange={(v) => update("floor", v)}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Alege" />
                </SelectTrigger>
                <SelectContent>
                  {FLOOR_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-stone-700">Din total etaje</Label>
              <Input
                type="number"
                placeholder="10"
                value={form.total_floors}
                onChange={(e) => update("total_floors", e.target.value)}
                className="mt-1.5"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-stone-700">Compartimentare</Label>
              <Select value={form.compartment} onValueChange={(v) => update("compartment", v)}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Alege" />
                </SelectTrigger>
                <SelectContent>
                  {COMPARTMENT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-stone-700">Confort</Label>
              <Select value={form.comfort} onValueChange={(v) => update("comfort", v)}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Alege" />
                </SelectTrigger>
                <SelectContent>
                  {COMFORT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-stone-700">An construcție</Label>
              <Input
                type="number"
                placeholder="2020"
                value={form.year_built}
                onChange={(e) => update("year_built", e.target.value)}
                className="mt-1.5"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-stone-700">Tip clădire</Label>
              <Select value={form.building_type} onValueChange={(v) => update("building_type", v)}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Alege tipul" />
                </SelectTrigger>
                <SelectContent>
                  {BUILDING_TYPE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-stone-700">Stare</Label>
              <Select value={form.property_condition} onValueChange={(v) => update("property_condition", v)}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Starea proprietății" />
                </SelectTrigger>
                <SelectContent>
                  {CONDITION_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-stone-700">Mobilare</Label>
              <Select value={form.furnished} onValueChange={(v) => update("furnished", v)}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Alege" />
                </SelectTrigger>
                <SelectContent>
                  {FURNISHED_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-stone-700">Loc parcare</Label>
              <div className="flex gap-2 mt-1.5">
                <button
                  type="button"
                  onClick={() => update("parking", true)}
                  className={`flex-1 py-2.5 rounded-lg font-medium transition-all ${
                    form.parking === true
                      ? "bg-emerald-500 text-white"
                      : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                >
                  Da
                </button>
                <button
                  type="button"
                  onClick={() => update("parking", false)}
                  className={`flex-1 py-2.5 rounded-lg font-medium transition-all ${
                    form.parking === false
                      ? "bg-stone-500 text-white"
                      : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                >
                  Nu
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SECTION: Energy Class - HIGHLIGHTED */}
      <section className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-stone-800">Certificat Energetic</h3>
            <p className="text-sm text-stone-500">
              Clasa energetică influențează dobânda creditului ipotecar
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-5 border border-emerald-200">
          <Label className="text-stone-700 font-medium mb-3 block">Clasa energetică a imobilului</Label>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {ENERGY_CLASSES.map(ec => (
              <button
                key={ec.value}
                type="button"
                onClick={() => update("energy_class", ec.value)}
                className={`relative px-4 py-3 rounded-lg font-bold transition-all ${
                  form.energy_class === ec.value
                    ? `${ec.color} text-white shadow-lg scale-105`
                    : "bg-white text-stone-600 border border-stone-200 hover:border-stone-300"
                }`}
              >
                {ec.value}
                {ec.benefit && form.energy_class !== ec.value && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-500 rounded-full"></span>
                )}
              </button>
            ))}
          </div>

          {/* Benefit info */}
          {selectedEnergyClass?.benefit && (
            <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-emerald-300">
              <TrendingDown className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div>
                <span className="font-medium text-emerald-700">{selectedEnergyClass.benefit}</span>
                <span className="text-stone-600 text-sm ml-1">
                  la creditele ipotecare "verzi"
                </span>
              </div>
            </div>
          )}

          {!form.energy_class && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm">
              <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-amber-800">
                <strong>Recomandat:</strong> Imobilele cu clasă energetică A, B sau C beneficiază de 
                reduceri ale dobânzii la creditele ipotecare oferite de băncile partenere.
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SECTION: Description */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-stone-800">Descriere</h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={generateAIDescription}
            disabled={generatingAI}
            className="text-purple-600 border-purple-300 hover:bg-purple-50"
          >
            {generatingAI ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Se generează...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generează cu AI
              </>
            )}
          </Button>
        </div>
        <div>
          <div className="flex justify-between items-center mb-1.5">
            <Label className="text-stone-700">Descrierea proprietății</Label>
            <span className="text-xs text-stone-400">{form.description.length}/3000</span>
          </div>
          <Textarea
            placeholder="Descrieți proprietatea în detaliu: finisaje, facilități, vecinătăți, avantaje, motive de vânzare..."
            value={form.description}
            onChange={(e) => update("description", e.target.value.slice(0, 3000))}
            rows={5}
            className="resize-none"
          />
          <p className="text-xs text-stone-500 mt-1.5">
            💡 Completează câmpurile de mai sus și apasă "Generează cu AI" pentru o descriere profesională
          </p>
        </div>
      </section>

      {/* SECTION: Images */}
      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-stone-800">Fotografii</h3>
        <ImageUploaderModern
          images={form.images}
          onImagesChange={(images) => update("images", images)}
          maxImages={15}
        />
      </section>

      {/* SECTION: Contact */}
      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
          <User className="w-5 h-5 text-emerald-600" />
          Date de contact
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-stone-700">Nume persoană contact</Label>
            <Input
              placeholder="Numele dvs."
              value={form.contact_name}
              onChange={(e) => update("contact_name", e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-stone-700">
              Telefon <span className="text-red-500">*</span>
            </Label>
            <Input
              placeholder="0721 123 456"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              className="mt-1.5"
            />
          </div>
        </div>
      </section>

      {/* Submit */}
      <div className="flex gap-3 pt-4 border-t">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
            Anulează
          </Button>
        )}
        <Button 
          type="button" 
          onClick={handleSubmit}
          disabled={saving}
          className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-semibold"
        >
          {saving ? "Se salvează..." : (listing ? "Salvează modificările" : "Publică anunțul")}
        </Button>
      </div>
    </div>
  );
};

export default ListingForm;
